import {useCallback, useEffect, useRef, useState} from 'react';
import {Modal} from 'react-bootstrap';
import QRCode from 'react-qr-code';
import {cpfCnpjMask} from '../Utils/Mask';
import {formatDate} from '../Utils/formatDate';
import logoImg from '../assets/netcomet2.png';
import {
	getSenhaTotem,
	limparSenhaTotem,
	mensagemErro,
	setSenhaTotem,
	totemApi,
	TotemCliente,
	TotemContrato,
	TotemFatura as TotemFaturaType,
} from '../services/TotemApi';
import {TotemDescanso} from '../components/Totem/TotemDescanso';
import {TotemTeclado} from '../components/Totem/TotemTeclado';
import {DestaqueFatura, TotemFatura} from '../components/Totem/TotemFatura';

import '../styles/totem.scss';

/**
 * Totem de atendimento da loja (rota /totem).
 *
 * Fluxo: bloqueado (senha do totem, só na primeira vez) -> descanso (propagandas)
 * -> documento (CPF/CNPJ) -> contratos (só se houver mais de um) -> faturas.
 * Qualquer inatividade volta para a tela de descanso e limpa os dados do cliente.
 */

type Etapa = 'bloqueado' | 'descanso' | 'documento' | 'contratos' | 'faturas';

const INATIVIDADE_SEGUNDOS = 60;

type ModalBoleto = {
	tipo: 'pix' | 'linha';
	fatura: TotemFaturaType;
	dados: string;
} | null;

function limparDocumento(valor: string) {
	return valor.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
}

function documentoValido(valor: string) {
	const limpo = limparDocumento(valor);
	return limpo.length === 11 || limpo.length === 14;
}

/** Converte o blob do PDF para base64 puro (sem o prefixo data:...), que é o que a ponte do app Android espera. */
function blobParaBase64(blob: Blob): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const resultado = String(reader.result || '');
			resolve(resultado.substring(resultado.indexOf(',') + 1));
		};
		reader.onerror = () => reject(reader.error);
		reader.readAsDataURL(blob);
	});
}

export function Totem() {
	const [etapa, setEtapa] = useState<Etapa>('bloqueado');
	const [senha, setSenha] = useState('');
	const [verificandoSenha, setVerificandoSenha] = useState(true);
	const [erro, setErro] = useState('');
	const [aviso, setAviso] = useState('');
	const [carregando, setCarregando] = useState(false);

	const [documento, setDocumento] = useState('');
	const [cliente, setCliente] = useState<TotemCliente | null>(null);
	const [contratos, setContratos] = useState<TotemContrato[]>([]);
	const [contrato, setContrato] = useState<TotemContrato | null>(null);
	const [faturas, setFaturas] = useState<TotemFaturaType[]>([]);

	const [acaoFatura, setAcaoFatura] = useState<{ id: number; tipo: string } | null>(null);
	const [modal, setModal] = useState<ModalBoleto>(null);
	const [segundosRestantes, setSegundosRestantes] = useState(INATIVIDADE_SEGUNDOS);

	const iframePrintRef = useRef<HTMLIFrameElement | null>(null);

	// ---------- senha do totem ----------

	useEffect(() => {
		const salva = getSenhaTotem();
		if (!salva) {
			setVerificandoSenha(false);
			return;
		}
		totemApi.liberar(salva)
			.then(() => setEtapa('descanso'))
			.catch((error) => {
				if (error?.response?.status === 401) {
					limparSenhaTotem();
				}
				setErro(error?.response ? mensagemErro(error) : 'Sem comunicação com o servidor. Verifique a rede e toque em Liberar.');
			})
			.then(() => setVerificandoSenha(false));
	}, []);

	function entrarTelaCheia() {
		const el: any = document.documentElement;
		const pedir = el.requestFullscreen || el.webkitRequestFullscreen;
		if (pedir && !document.fullscreenElement) {
			try {
				pedir.call(el).catch(() => {});
			} catch (e) {
			}
		}
	}

	function handleLiberar() {
		const valor = senha.trim() || getSenhaTotem();
		if (!valor) {
			setErro('Informe a senha do totem');
			return;
		}
		setCarregando(true);
		setErro('');
		totemApi.liberar(valor)
			.then(() => {
				setSenhaTotem(valor);
				setSenha('');
				entrarTelaCheia();
				setEtapa('descanso');
			})
			.catch((error) => setErro(mensagemErro(error, 'Senha inválida')))
			.then(() => setCarregando(false));
	}

	// ---------- fluxo do cliente ----------

	const reiniciar = useCallback(() => {
		setDocumento('');
		setCliente(null);
		setContratos([]);
		setContrato(null);
		setFaturas([]);
		setErro('');
		setAviso('');
		setModal(null);
		setAcaoFatura(null);
		setCarregando(false);
		setEtapa('descanso');
	}, []);

	function handleTocar() {
		entrarTelaCheia();
		setErro('');
		setEtapa('documento');
	}

	/** Equipe da loja: apaga a senha salva e volta para a tela de liberação. */
	const bloquear = useCallback(() => {
		limparSenhaTotem();
		setSenha('');
		setErro('');
		setEtapa('bloqueado');
	}, []);

	function handleTecla(tecla: string) {
		setErro('');
		setDocumento(atual => cpfCnpjMask(limparDocumento(atual) + tecla));
	}

	function handleApagar() {
		setErro('');
		setDocumento(atual => cpfCnpjMask(limparDocumento(atual).slice(0, -1)));
	}

	const carregarFaturas = useCallback((doc: string, contratoEscolhido: TotemContrato) => {
		setCarregando(true);
		setErro('');
		totemApi.faturas(doc, contratoEscolhido.id_contrato)
			.then((lista) => {
				setFaturas(lista);
				setContrato(contratoEscolhido);
				setEtapa('faturas');
			})
			.catch((error) => setErro(mensagemErro(error, 'Não foi possível buscar as faturas')))
			.then(() => setCarregando(false));
	}, []);

	function handleConsultar() {
		if (!documentoValido(documento)) {
			setErro('Digite um CPF ou CNPJ válido');
			return;
		}
		const doc = limparDocumento(documento);
		setCarregando(true);
		setErro('');
		totemApi.cliente(doc)
			.then((resposta) => {
				setCliente(resposta.cliente);
				setContratos(resposta.contratos);
				if (resposta.contratos.length === 0) {
					setErro('Nenhum contrato encontrado para este documento');
					setCarregando(false);
				} else if (resposta.contratos.length === 1) {
					carregarFaturas(doc, resposta.contratos[0]);
				} else {
					setEtapa('contratos');
					setCarregando(false);
				}
			})
			.catch((error) => {
				setErro(mensagemErro(error, 'Cliente não encontrado'));
				setCarregando(false);
			});
	}

	function handleEscolherContrato(c: TotemContrato) {
		carregarFaturas(limparDocumento(documento), c);
	}

	// ---------- ações do boleto ----------

	/**
	 * Dentro do app Android do totem (window.TotemAndroid) o PDF vai em base64 para a ponte,
	 * que imprime em silêncio na impressora da loja. Fora do app, cai no iframe + window.print().
	 */
	function handleImprimir(fatura: TotemFaturaType) {
		setAcaoFatura({id: fatura.id, tipo: 'pdf'});
		setErro('');
		setAviso('');
		totemApi.boletoPdf(limparDocumento(documento), fatura.id)
			.then((blob) => {
				const ponte = window.TotemAndroid;
				if (ponte) {
					if (!ponte.configurado()) {
						setErro('Impressora não configurada no totem. Chame a recepção.');
						return;
					}
					return blobParaBase64(blob).then((base64) => {
						ponte.imprimirPdf(base64, `boleto-${fatura.numero_documento || fatura.id}.pdf`);
						setAviso('Boleto enviado para a impressora. Retire na recepção.');
					});
				}
				const url = window.URL.createObjectURL(blob);
				const iframe = iframePrintRef.current;
				if (!iframe) {
					return;
				}
				iframe.onload = () => {
					try {
						iframe.contentWindow?.focus();
						iframe.contentWindow?.print();
					} catch (e) {
						setErro('Não foi possível enviar para a impressora');
					}
					window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
				};
				iframe.src = url;
			})
			.catch((error) => setErro(mensagemErro(error, 'Não foi possível gerar o boleto')))
			.then(() => setAcaoFatura(null));
	}

	function handlePix(fatura: TotemFaturaType) {
		setAcaoFatura({id: fatura.id, tipo: 'pix'});
		setErro('');
		totemApi.qrcode(limparDocumento(documento), fatura.id)
			.then((dados) => {
				if (!dados) {
					setErro('QR Code PIX indisponível para este boleto');
					return;
				}
				setModal({tipo: 'pix', fatura, dados});
			})
			.catch((error) => setErro(mensagemErro(error, 'Não foi possível gerar o PIX')))
			.then(() => setAcaoFatura(null));
	}

	function handleLinhaDigitavel(fatura: TotemFaturaType) {
		setAcaoFatura({id: fatura.id, tipo: 'linha'});
		setErro('');
		totemApi.linhaDigitavel(limparDocumento(documento), fatura.id)
			.then((dados) => {
				if (!dados) {
					setErro('Código do boleto indisponível');
					return;
				}
				setModal({tipo: 'linha', fatura, dados});
			})
			.catch((error) => setErro(mensagemErro(error, 'Não foi possível buscar o código do boleto')))
			.then(() => setAcaoFatura(null));
	}

	// ---------- resultado da impressão vindo do app Android ----------

	useEffect(() => {
		const aoImprimir = (evento: CustomEvent<TotemImpressaoDetalhe>) => {
			const detalhe = evento.detail || {ok: false, mensagem: ''};
			if (detalhe.ok) {
				return;
			}
			setAviso('');
			setErro(`Não foi possível imprimir o boleto${detalhe.mensagem ? `: ${detalhe.mensagem}` : ''}`);
		};
		window.addEventListener('totem-impressao', aoImprimir);
		return () => window.removeEventListener('totem-impressao', aoImprimir);
	}, []);

	useEffect(() => {
		if (!aviso) {
			return;
		}
		const timer = window.setTimeout(() => setAviso(''), 8000);
		return () => window.clearTimeout(timer);
	}, [aviso]);

	// ---------- inatividade ----------

	const emAtendimento = etapa === 'documento' || etapa === 'contratos' || etapa === 'faturas';

	useEffect(() => {
		if (!emAtendimento) {
			return;
		}
		let restantes = INATIVIDADE_SEGUNDOS;
		setSegundosRestantes(restantes);

		const renovar = () => {
			restantes = INATIVIDADE_SEGUNDOS;
			setSegundosRestantes(restantes);
		};
		const eventos: Array<keyof DocumentEventMap> = ['pointerdown', 'touchstart', 'keydown', 'mousemove'];
		eventos.forEach(ev => document.addEventListener(ev, renovar));

		const intervalo = window.setInterval(() => {
			restantes -= 1;
			setSegundosRestantes(restantes);
			if (restantes <= 0) {
				reiniciar();
			}
		}, 1000);

		return () => {
			eventos.forEach(ev => document.removeEventListener(ev, renovar));
			window.clearInterval(intervalo);
		};
	}, [emAtendimento, etapa, reiniciar]);

	// ---------- faturas: ordenação e destaque ----------

	// Em aberto, mostra todas as vencidas e só a próxima a vencer (as demais a vencer ficam de fora)
	const porVencimento = faturas
		.filter(f => f.status === 'aberto' || f.status === 'vencido')
		.sort((a, b) => a.data_vencimento.localeCompare(b.data_vencimento));
	const proxima = porVencimento.find(f => f.status === 'aberto');
	const abertas = porVencimento.filter(f => f.status === 'vencido' || f === proxima);
	const pagas = faturas
		.filter(f => f.status === 'pago')
		.sort((a, b) => b.data_vencimento.localeCompare(a.data_vencimento));
	const idProxima = proxima?.id;
	const vencidasQtd = abertas.filter(f => f.status === 'vencido').length;
	const aVencerQtd = abertas.length - vencidasQtd;

	function destaqueDe(f: TotemFaturaType): DestaqueFatura {
		if (f.status === 'vencido') return 'vencida';
		if (f.id === idProxima) return 'proxima';
		return 'normal';
	}

	// ---------- render ----------

	if (etapa === 'bloqueado') {
		return (
			<div className="totem totem-atendimento totem-bloqueado">
				<div className="totem-fundo" aria-hidden="true">
					<div className="totem-fundo-ondas"/>
					<div className="totem-fundo-pontos totem-fundo-pontos-1"/>
					<div className="totem-fundo-pontos totem-fundo-pontos-2"/>
					<div className="totem-fundo-arco"/>
				</div>
				<div className="totem-bloqueado-conteudo">
					<img src={logoImg} alt="Netcomet" className="totem-bloqueado-logo"/>
					<div className="totem-bloqueado-card">
						<span className="totem-bloqueado-icone" aria-hidden="true">
							<span className="material-icons">{verificandoSenha ? 'hourglass_top' : 'lock'}</span>
						</span>
						<h1>Totem de atendimento</h1>
						{verificandoSenha && <p>Verificando a senha salva...</p>}
						{!verificandoSenha && (
							<>
								<input
									type="password"
									className="totem-input"
									placeholder="Senha do totem"
									value={senha}
									autoFocus
									onChange={e => setSenha(e.target.value)}
									onKeyDown={e => { if (e.key === 'Enter') handleLiberar(); }}
								/>
								{erro && <div className="totem-erro">{erro}</div>}
								<button type="button" className="totem-botao totem-botao-primario" onClick={handleLiberar} disabled={carregando}>
									<span className="material-icons" aria-hidden="true">lock_open</span>
									{carregando ? 'Verificando...' : 'Liberar totem'}
								</button>
							</>
						)}
					</div>
				</div>
			</div>
		);
	}

	if (etapa === 'descanso') {
		return (
			<div className="totem">
				<TotemDescanso onTocar={handleTocar} onBloquear={bloquear}/>
			</div>
		);
	}

	return (
		<div className="totem totem-atendimento">
			<div className="totem-fundo" aria-hidden="true">
				<div className="totem-fundo-ondas"/>
				<div className="totem-fundo-pontos totem-fundo-pontos-1"/>
				<div className="totem-fundo-pontos totem-fundo-pontos-2"/>
				<div className="totem-fundo-arco"/>
			</div>
			<header className="totem-header">
				<img src={logoImg} alt="Netcomet" className="totem-header-logo"/>
				<div className="totem-header-cliente">
					{cliente && <strong>{cliente.nome}</strong>}
					{contrato && <span>Contrato {contrato.numero_contrato}</span>}
				</div>
				<div className="totem-header-acoes">
					<span className="totem-header-contador" title="Tempo até voltar ao início">{segundosRestantes}s</span>
					<button type="button" className="totem-botao totem-botao-claro" onClick={reiniciar}>
						<span className="material-icons" aria-hidden="true">home</span>
						Início
					</button>
				</div>
			</header>

			<main className={`totem-conteudo ${etapa === 'documento' ? 'totem-conteudo-centro' : ''}`}>
				{etapa === 'documento' && (
					<div className="totem-documento">
						<div className="totem-documento-ola">
							<span className="totem-documento-emoji" aria-hidden="true">👋</span>
							<h1>Oi! Vamos achar<br/>seus boletos?</h1>
							<p>Digite seu CPF ou CNPJ e a gente mostra o que está em aberto. Sem senha, sem telefone.</p>
							<ul className="totem-documento-dicas">
								<li><span className="material-icons" aria-hidden="true">print</span>Imprime o boleto na hora</li>
								<li><span className="material-icons" aria-hidden="true">pix</span>Paga com PIX pelo celular</li>
								<li><span className="material-icons" aria-hidden="true">visibility_off</span>Seus dados somem da tela quando você sai</li>
							</ul>
						</div>
						<div className="totem-documento-card">
							<label className="totem-documento-label" htmlFor="totem-documento-input">Seu CPF ou CNPJ</label>
							<input
								id="totem-documento-input"
								type="text"
								className="totem-input totem-input-documento"
								value={documento}
								placeholder="digite aqui"
								inputMode="none"
								autoComplete="off"
								onChange={e => { setErro(''); setDocumento(cpfCnpjMask(e.target.value)); }}
								onKeyDown={e => { if (e.key === 'Enter') handleConsultar(); }}
							/>
							{erro && <div className="totem-erro">{erro}</div>}
							<TotemTeclado
								onTecla={handleTecla}
								onApagar={handleApagar}
								onLimpar={() => { setDocumento(''); setErro(''); }}
								onConfirmar={handleConsultar}
								confirmarHabilitado={documentoValido(documento)}
								carregando={carregando}
							/>
						</div>
					</div>
				)}

				{etapa === 'contratos' && (
					<div className="totem-contratos">
						<div className="totem-titulo">
							<h1>Qual contrato você quer ver?</h1>
							<p>Encontramos {contratos.length} contratos no seu nome. Toque em um para ver os boletos.</p>
						</div>
						{erro && <div className="totem-erro">{erro}</div>}
						<div className="totem-contratos-lista">
							{contratos.map(c => (
								<button
									key={c.id_contrato}
									type="button"
									className={`totem-contrato ${c.faturas_vencidas > 0 ? 'totem-contrato-vencido' : ''}`}
									disabled={carregando}
									onClick={() => handleEscolherContrato(c)}
								>
									<span className="totem-contrato-icone material-icons" aria-hidden="true">{c.faturas_vencidas > 0 ? 'priority_high' : 'home'}</span>
									<div className="totem-contrato-info">
										<div className="totem-contrato-plano">{c.plano}</div>
										<div className="totem-contrato-endereco">
											<span className="material-icons" aria-hidden="true">place</span>
											<span>
												{[c.endereco, c.endereco_numero].filter(Boolean).join(', ')}
												{c.bairro ? ` · ${c.bairro}` : ''}
												{c.cidade ? ` · ${c.cidade}` : ''}
											</span>
										</div>
										<div className="totem-contrato-detalhe">
											<span className="totem-contrato-numero">Contrato {c.numero_contrato}</span>
											<span className={`totem-contrato-status ${c.status === 'Ativo' ? 'ativo' : ''}`}>{c.status}</span>
										</div>
									</div>
									<div className="totem-contrato-resumo">
										{c.faturas_vencidas > 0 && <span className="totem-badge totem-badge-vencida">{c.faturas_vencidas} vencida{c.faturas_vencidas > 1 ? 's' : ''}</span>}
										{c.faturas_vencidas === 0 && c.faturas_em_aberto > 0 && c.proximo_vencimento && <span className="totem-badge totem-badge-proxima">Vence {formatDate(c.proximo_vencimento)}</span>}
										{c.faturas_em_aberto === 0 && <span className="totem-badge totem-badge-ok">Em dia</span>}
										<span className="material-icons totem-contrato-seta" aria-hidden="true">chevron_right</span>
									</div>
								</button>
							))}
						</div>
					</div>
				)}

				{etapa === 'faturas' && (
					<div className="totem-faturas">
						{contratos.length > 1 && (
							<button type="button" className="totem-link" onClick={() => { setErro(''); setEtapa('contratos'); }}>
								<span className="material-icons" aria-hidden="true">keyboard_arrow_left</span>
								Trocar contrato
							</button>
						)}
						<div className="totem-titulo">
							<h1>Seus boletos</h1>
							{contrato && <p>Contrato {contrato.numero_contrato} · {contrato.plano}</p>}
							{!carregando && faturas.length > 0 && (
								<div className="totem-resumo">
									{vencidasQtd > 0 && <span className="totem-badge totem-badge-vencida">{vencidasQtd} vencida{vencidasQtd > 1 ? 's' : ''}</span>}
									{aVencerQtd > 0 && <span className="totem-badge totem-badge-proxima">{aVencerQtd} a vencer</span>}
									{vencidasQtd === 0 && aVencerQtd === 0 && <span className="totem-badge totem-badge-ok">Tudo em dia</span>}
								</div>
							)}
						</div>
						{erro && <div className="totem-erro">{erro}</div>}
						{aviso && (
							<div className="totem-aviso">
								<span className="material-icons" aria-hidden="true">print</span>
								{aviso}
							</div>
						)}
						{carregando && <p className="totem-carregando">Carregando...</p>}
						{!carregando && abertas.length === 0 && (
							<div className="totem-faturas-vazio">
								<span className="material-icons" aria-hidden="true">check_circle</span>
								<p>Nenhum boleto em aberto. Está tudo em dia!</p>
							</div>
						)}
						{abertas.map(f => (
							<TotemFatura
								key={f.id}
								fatura={f}
								destaque={destaqueDe(f)}
								carregando={acaoFatura && acaoFatura.id === f.id ? acaoFatura.tipo : null}
								onImprimir={handleImprimir}
								onPix={handlePix}
								onLinhaDigitavel={handleLinhaDigitavel}
							/>
						))}
						{pagas.length > 0 && (
							<details className="totem-faturas-pagas">
								<summary>Pagas recentemente ({pagas.length})</summary>
								{pagas.map(f => (
									<TotemFatura
										key={f.id}
										fatura={f}
										destaque="paga"
										carregando={null}
										onImprimir={() => {}}
										onPix={() => {}}
										onLinhaDigitavel={() => {}}
									/>
								))}
							</details>
						)}
					</div>
				)}
			</main>

			<iframe ref={iframePrintRef} title="impressao-boleto" className="totem-iframe-impressao"/>

			<Modal show={modal !== null} onHide={() => setModal(null)} centered size="lg" dialogClassName="totem-modal">
				<Modal.Header className="justify-content-center border-bottom-0">
					<Modal.Title>{modal?.tipo === 'pix' ? 'Pagamento PIX' : 'Código do boleto'}</Modal.Title>
				</Modal.Header>
				<Modal.Body className="text-center">
					{modal && (
						<>
							<p className="totem-modal-vencimento">Vencimento {formatDate(modal.fatura.data_vencimento)} · R$ {modal.fatura.valor_total.toFixed(2).replace('.', ',')}</p>
							{modal.tipo === 'pix' && (
								<>
									<p>Aponte a câmera do aplicativo do seu banco para o QR Code</p>
									<div className="totem-modal-qrcode"><QRCode value={modal.dados} size={320}/></div>
								</>
							)}
							{modal.tipo === 'linha' && (
								<>
									<p>Digite este código no aplicativo do seu banco</p>
									<div className="totem-modal-linha">{modal.dados}</div>
								</>
							)}
						</>
					)}
				</Modal.Body>
				<Modal.Footer className="border-top-0 justify-content-center">
					<button type="button" className="totem-botao totem-botao-primario" onClick={() => setModal(null)}>Fechar</button>
				</Modal.Footer>
			</Modal>
		</div>
	);
}
